import { initializeApp } from 'firebase/app';
import { getMessaging, getToken } from 'firebase/messaging';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';

const firebaseConfig = {
  apiKey: "AIzaSyCTbZq-IY7zDZIoV1DNVrUrOLFPx46sf2I",
  authDomain: "at-last-496da.firebaseapp.com",
  projectId: "at-last-496da",
  storageBucket: "at-last-496da.firebasestorage.app",
  messagingSenderId: "445083718713",
  appId: "1:445083718713:web:d17087a05e5d81215a29e8"
};

const firebaseApp = initializeApp(firebaseConfig);

// VAPID key (Firebase Console → Project Settings → Cloud Messaging →
// Web configuration → key pair) — already generated and set below.
// Only used on the web path below — native push doesn't use VAPID at
// all, it authenticates via APNs/FCM platform credentials configured
// directly in each store's build, not this key.
const VAPID_KEY = "BLtis8P0hZeQl5sFRot3N7kOugYS3P_EwG4ZoYMwXUKQLBgs0INCNihf2uFUgPw0oRMIDGwycC7zC5vhx08Y1dI";

/**
 * Requests notification permission and returns a device token if
 * granted, or null if denied/unsupported. Call this from a real user
 * interaction (a button click), not automatically on page load — browsers
 * increasingly ignore or auto-deny permission prompts that aren't tied to
 * a deliberate user action, and an unprompted popup is also just a worse
 * experience than one the user asked for.
 *
 * Branches by platform (Aug 23, per Ali's report, added for the
 * Capacitor mobile build): inside a real native app this uses
 * @capacitor/push-notifications (real APNs/FCM registration, not
 * browser Web Push, which doesn't exist inside a native WebView).
 * Outside native — the website, or the app opened in a regular mobile
 * or desktop browser — this is completely unchanged from before,
 * still Firebase's web SDK with the VAPID key. The caller
 * (Dashboard.js's handleEnableNotifications) doesn't need to know or
 * care which path ran; both resolve to the same shape, a token string
 * or null, and saveDeviceToken() on the backend already treats any
 * valid FCM registration token the same regardless of which platform
 * it came from — the native plugin also registers through FCM under
 * the hood, so the backend's send logic needed no changes at all.
 */
export async function requestNotificationPermission() {
  if (Capacitor.isNativePlatform()) {
    return requestNativeNotificationPermission();
  }
  return requestWebNotificationPermission();
}

/**
 * True when this device has already granted notification permission, so
 * the Dashboard can quietly re-register its token and skip the
 * "Turn on phone alerts" prompt (Oct 10, per Ali's report: the prompt
 * kept showing on every visit even after alerts were on, because
 * nothing checked the existing permission on load). Never prompts.
 */
export async function notificationsAlreadyGranted() {
  try {
    if (Capacitor.isNativePlatform()) {
      const st = await PushNotifications.checkPermissions();
      return st.receive === 'granted';
    }
    return 'Notification' in window && Notification.permission === 'granted';
  } catch {
    return false;
  }
}

function requestNativeNotificationPermission() {
  return new Promise((resolve) => {
    (async () => {
      try {
        const permStatus = await PushNotifications.requestPermissions();
        if (permStatus.receive !== 'granted') {
          resolve(null);
          return;
        }

        // Unlike the web path's getToken(), which directly returns a
        // token, the native plugin delivers it asynchronously via an
        // event — register() itself only resolves once registration
        // has been *requested*, not once a token has actually arrived.
        // Both listeners get removed as soon as either fires, so this
        // only ever resolves once and doesn't leak a dangling listener.
        let successListener;
        let errorListener;

        successListener = await PushNotifications.addListener('registration', (token) => {
          successListener?.remove();
          errorListener?.remove();
          resolve(token.value || null);
        });

        errorListener = await PushNotifications.addListener('registrationError', (err) => {
          successListener?.remove();
          errorListener?.remove();
          console.warn('Native push registration failed:', err);
          resolve(null);
        });

        await PushNotifications.register();
      } catch (error) {
        // Same treat-as-unavailable-not-a-hard-failure reasoning as the
        // web path below — a denied permission or an unsupported device
        // shouldn't alarm the user, just leave notifications off.
        console.warn('Native push permission/registration failed:', error);
        resolve(null);
      }
    })();
  });
}

async function requestWebNotificationPermission() {
  if (!('Notification' in window)) {
    console.warn('This browser does not support notifications.');
    return null;
  }

  try {
    const permission = await Notification.requestPermission();

    if (permission !== 'granted') {
      console.log('Notification permission denied.');
      return null;
    }

    const messaging = getMessaging(firebaseApp);
    const token = await getToken(messaging, { vapidKey: VAPID_KEY });

    return token || null;
  } catch (error) {
    // Common on iOS Safari if the app hasn't been added to the home
    // screen yet — push simply isn't available in a regular browser tab
    // there, regardless of permission. Caller should treat null the same
    // as "not available right now," not as a hard failure to alarm the
    // user over.
    console.warn('Notification permission/token request failed:', error);
    return null;
  }
}